import { z } from 'zod';
// Canvas element style schema
export const canvasElementStyleSchema = z.object({
    fill: z.string().optional(),
    stroke: z.string().optional(),
    strokeWidth: z.number().min(0).optional(),
    opacity: z.number().min(0).max(1).optional(),
    fontSize: z.number().min(1).optional(),
    fontFamily: z.string().optional(),
    fontWeight: z.string().optional(),
    textAlign: z.string().optional(),
    borderRadius: z.number().min(0).optional(),
}).strict();
// Canvas position
export const canvasPositionSchema = z.object({
    x: z.number(),
    y: z.number(),
});
// Canvas size
export const canvasSizeSchema = z.object({
    width: z.number().min(1),
    height: z.number().min(1),
});
// Canvas element schema
export const canvasElementSchema = z.object({
    id: z.string().min(1),
    type: z.enum(['rect', 'circle', 'line', 'arrow', 'text', 'image', 'group', 'table', 'chart']),
    position: canvasPositionSchema,
    size: canvasSizeSchema,
    rotation: z.number().default(0),
    style: canvasElementStyleSchema.default({}),
    content: z.string().optional(),
    src: z.string().optional(),
    children: z.array(z.string()).optional(),
    zIndex: z.number().default(0),
    locked: z.boolean().default(false),
    visible: z.boolean().default(true),
    name: z.string().optional(),
    chartType: z.enum(['bar', 'line', 'pie']).optional(),
    chartData: z.record(z.unknown()).optional(),
    tableData: z.array(z.array(z.string())).optional(),
    tableColumns: z.number().int().min(1).optional(),
    tableRows: z.number().int().min(1).optional(),
    points: z.array(z.number()).optional(),
});
// Canvas element create schema (for API: id, zIndex, locked, visible are server-generated)
export const canvasElementCreateSchema = z.object({
    type: z.enum(['rect', 'circle', 'line', 'arrow', 'text', 'image', 'group', 'table', 'chart']),
    position: canvasPositionSchema.optional(),
    size: canvasSizeSchema.optional(),
    rotation: z.number().default(0),
    style: canvasElementStyleSchema.default({}),
    content: z.string().optional(),
    src: z.string().optional(),
    name: z.string().optional(),
    chartType: z.enum(['bar', 'line', 'pie']).optional(),
    chartData: z.record(z.unknown()).optional(),
    tableData: z.array(z.array(z.string())).optional(),
    tableColumns: z.number().int().min(1).optional(),
    tableRows: z.number().int().min(1).optional(),
    points: z.array(z.number()).optional(),
});
// Content type
export const canvasContentTypeSchema = z.enum(['drawing', 'html', 'pixel']);
// Project create
export const canvasProjectCreateSchema = z.object({
    title: z.string().min(1, 'Proje adı zorunludur').max(255),
    description: z.string().max(1000).optional(),
    width: z.coerce.number().int().min(100).max(10000).optional().default(1200),
    height: z.coerce.number().int().min(100).max(10000).optional().default(800),
    backgroundColor: z.string().optional().default('#ffffff'),
    contentType: canvasContentTypeSchema.optional().default('drawing'),
});
// Project update
export const canvasProjectUpdateSchema = z.object({
    title: z.string().min(1).max(255).optional(),
    description: z.string().max(1000).optional().nullable(),
    width: z.coerce.number().int().min(100).max(10000).optional(),
    height: z.coerce.number().int().min(100).max(10000).optional(),
    backgroundColor: z.string().optional(),
    elements: z.array(canvasElementSchema).optional(),
    isPublic: z.boolean().optional(),
    contentType: canvasContentTypeSchema.optional(),
});
// HTML content update schema (for AI tool and API)
export const canvasHtmlUpdateSchema = z.object({
    htmlContent: z.string().min(1).max(5_000_000),
    cssContent: z.string().max(500_000).optional().nullable(),
});
// Data source schema
export const canvasDataSourceSchema = z.object({
    id: z.string().min(1),
    type: z.enum(['sql', 'api', 'static']),
    name: z.string().min(1).max(100),
    config: z.record(z.unknown()),
    lastFetched: z.string().optional(),
});
// Project list query
export const canvasProjectListQuerySchema = z.object({
    page: z.coerce.number().int().positive().optional().default(1),
    perPage: z.coerce.number().int().min(1).max(100).optional().default(20),
    search: z.string().optional(),
});
// --- Pixel Canvas Schemas ---
// Pixel data: sparse map of "x,y" -> palette index
export const pixelDataSchema = z.record(z.string().regex(/^\d+,\d+$/), z.number().int().min(0));
// Pixel palette: array of hex color strings
export const pixelPaletteSchema = z.array(z.string().regex(/^#[0-9a-fA-F]{6}$/)).min(1).max(256);
// Set pixels operation (batch)
export const pixelSetSchema = z.object({
    pixels: z.array(z.object({
        x: z.number().int().min(0),
        y: z.number().int().min(0),
        colorIndex: z.number().int().min(0),
    })).min(1).max(10000),
});
// Fill area operation
export const pixelFillAreaSchema = z.object({
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    width: z.number().int().min(1).max(256),
    height: z.number().int().min(1).max(256),
    colorIndex: z.number().int().min(0),
});
// Clear pixels operation
export const pixelClearSchema = z.object({
    pixels: z.array(z.object({
        x: z.number().int().min(0),
        y: z.number().int().min(0),
    })).min(1).max(10000).optional(),
    all: z.boolean().optional(),
});
// Update palette
export const pixelPaletteUpdateSchema = z.object({
    palette: pixelPaletteSchema,
});
// Pixel project create (extends base)
export const pixelProjectCreateSchema = z.object({
    title: z.string().min(1, 'Proje adı zorunludur').max(255),
    description: z.string().max(1000).optional(),
    gridWidth: z.coerce.number().int().min(8).max(256).optional().default(32),
    gridHeight: z.coerce.number().int().min(8).max(256).optional().default(32),
    backgroundColor: z.string().optional().default('#ffffff'),
    palette: pixelPaletteSchema.optional(),
});
//# sourceMappingURL=canvas.schema.js.map